<template>
  <div>
    <div v-if="visibleStoreEventNotes?.length > 0">
      <div class="relative">
        <div
          class="absolute left-4 sm:left-7 top-0 bottom-0 w-0.5 bg-linear-to-b/srgb from-blue-200 via-purple-200 to-gray-200"
        ></div>

        <div class="space-y-2 sm:space-y-3">
          <div v-for="(storeEventNote, index) in visibleStoreEventNotes" :key="storeEventNote.id" class="relative">
            <div class="absolute left-2 sm:left-4 top-2">
              <div class="group relative">
                <div
                  :class="[
                    'w-3 h-3 sm:w-7 sm:h-7 rounded-full border-2 border-white shadow-xs transition-all duration-200',
                    getIconClasses(storeEventNote.noteType),
                  ]"
                >
                  <component
                    :is="getRelatedIcon(storeEventNote.noteType)"
                    class="w-1.5 h-1.5 sm:w-2 sm:h-2 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                  />
                </div>
                <div
                  class="absolute -inset-1 sm:-inset-2 bg-blue-50 rounded-full opacity-0 transition-opacity duration-200"
                ></div>
              </div>
            </div>

            <div class="ml-8 sm:ml-12">
              <div
                class="bg-gray-50 hover:bg-gray-100 rounded-lg p-2 sm:p-3 transition-all duration-200 border border-gray-200 hover:border-gray-300"
              >
                <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-0">
                  <div class="flex-1 min-w-0">
                    <p
                      :class="[
                        'text-xs sm:text-sm font-medium text-gray-900 break-words',
                        index === 0 ? 'text-blue-900 font-semibold' : '',
                      ]"
                    >
                      {{ $t(`storeEventNote.noteType.${storeEventNote.noteType}`) }}
                    </p>
                    <p v-if="storeEventNote.content" class="text-xs text-gray-600 mt-1 break-words">
                      {{ storeEventNote.content }}
                    </p>
                    <p v-if="storeEventNote.estalyManager?.fullName" class="text-xs italic text-gray-500 mt-1">
                      par {{ storeEventNote.estalyManager.fullName }}
                    </p>
                  </div>
                  <div class="flex items-center justify-between sm:justify-end space-x-2 sm:space-x-3">
                    <span
                      :class="[
                        'px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-blue-100 text-blue-800',
                      ]"
                    >
                      {{ storeEventNote.date }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Show more/less button -->
          <div v-if="hasMoreNotes" class="relative">
            <div class="ml-8 sm:ml-12">
              <button
                @click="toggleShowAllNotes"
                class="w-full bg-gray-50 hover:bg-gray-100 rounded-lg p-2 sm:p-3 transition-all duration-200 border border-gray-200 hover:border-gray-300 text-center"
              >
                <div class="flex items-center justify-center space-x-2">
                  <span class="text-xs sm:text-sm font-medium text-gray-600">
                    {{ showAllNotes ? "Voir moins" : `Voir ${storeEventNotes.length - 2} éléments de plus` }}
                  </span>
                  <ChevronRightIcon
                    :class="['w-3 h-3 sm:w-4 sm:h-4 text-gray-400 transition-transform duration-200']"
                  />
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No events message -->
    <div v-if="storeEventNotes?.length === 0" class="bg-white flex items-center justify-center">
      <p class="p-4 font-medium text-center">Pas d’événements liés à ce marchand</p>
    </div>
  </div>
</template>

<script>
import { CalendarIcon, HandIcon, MailIcon, PhoneIcon, ChevronRightIcon } from "@heroicons/vue/solid";
import { PlusCircleIcon } from "@heroicons/vue/outline";

export default {
  components: {
    ChevronRightIcon,
  },

  props: {
    store: { type: Object, required: true },
  },

  computed: {
    storeEventNotes() {
      return this.store.storeEventNotes || [];
    },
    visibleStoreEventNotes() {
      if (this.showAllNotes) {
        return this.storeEventNotes;
      }
      // Show only the first 2 items (most recent)
      return this.storeEventNotes.slice(0, 2);
    },
    hasMoreNotes() {
      return this.storeEventNotes.length > 2;
    },
  },
  data() {
    return {
      showAllNotes: false,
    };
  },

  methods: {
    toggleShowAllNotes() {
      this.showAllNotes = !this.showAllNotes;
    },

    getIconClasses(noteType) {
      // Map different note types to appropriate background colors
      let result = "bg-gray-400";

      switch (noteType) {
        case "call":
          result = "bg-blue-500";
          break;
        case "visio":
          result = "bg-purple-500";
          break;
        case "message":
          result = "bg-green-500";
          break;
        case "visit":
          result = "bg-orange-500";
          break;
        case "other":
          result = "bg-gray-500";
          break;
      }

      return result;
    },

    getRelatedIcon(noteType) {
      let result = PlusCircleIcon;
      switch (noteType) {
        case "call":
          result = PhoneIcon;
          break;
        case "visio":
          result = CalendarIcon;
          break;
        case "message":
          result = MailIcon;
          break;
        case "visit":
          result = HandIcon;
          break;
        case "other":
          result = PlusCircleIcon;
          break;
      }

      return result;
    },
  },
};
</script>
